import { useState } from "react";
import { Copy, Link2, UserPlus, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { api, type User } from "../lib/api";
import { Button, Input } from "./ui";

export function InviteModal({
  open,
  onClose,
  meetingId,
  token,
}: {
  open: boolean;
  onClose: () => void;
  meetingId: string;
  token: string;
}) {
  const [guestUrl, setGuestUrl] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [users, setUsers] = useState<User[]>([]);
  const [status, setStatus] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function createGuestLink() {
    setLoading(true);
    setStatus(null);
    try {
      const { url } = await api.createGuestInvite(token, meetingId);
      setGuestUrl(url);
    } catch (e) {
      setStatus(e instanceof Error ? e.message : "Failed");
    } finally {
      setLoading(false);
    }
  }

  async function searchUsers() {
    if (!query.trim()) return;
    const { users: found } = await api.searchUsers(token, query);
    setUsers(found);
  }

  async function inviteUser(userId: string) {
    try {
      await api.inviteUser(token, meetingId, userId);
      setStatus("Invite sent to registered user");
    } catch (e) {
      setStatus(e instanceof Error ? e.message : "Failed");
    }
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="glass-panel w-full max-w-lg rounded-2xl p-6"
            initial={{ scale: 0.96, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.96, opacity: 0 }}
          >
            <div className="mb-4 flex items-start justify-between gap-3">
              <div>
                <h2 className="font-display text-lg font-semibold">Invite people</h2>
                <p className="text-sm text-ink-500">Only invited guests or users can join.</p>
              </div>
              <button type="button" onClick={onClose} className="rounded-lg p-1 hover:bg-white/10">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="rounded-xl bg-white/5 p-4 ring-1 ring-white/10">
                <div className="mb-2 flex items-center gap-2 text-sm font-medium">
                  <Link2 className="h-4 w-4 text-accent-400" />
                  Guest link
                </div>
                {guestUrl ? (
                  <div className="flex gap-2">
                    <Input readOnly value={guestUrl} className="text-xs" />
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => navigator.clipboard.writeText(guestUrl)}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <Button type="button" onClick={createGuestLink} disabled={loading}>
                    Generate invite link
                  </Button>
                )}
              </div>

              <div className="rounded-xl bg-white/5 p-4 ring-1 ring-white/10">
                <div className="mb-2 flex items-center gap-2 text-sm font-medium">
                  <UserPlus className="h-4 w-4 text-accent-400" />
                  Registered users
                </div>
                <div className="flex gap-2">
                  <Input
                    placeholder="Search by name or email"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && void searchUsers()}
                  />
                  <Button type="button" variant="ghost" onClick={() => void searchUsers()}>
                    Search
                  </Button>
                </div>
                <ul className="mt-3 max-h-40 space-y-1 overflow-y-auto">
                  {users.map((u) => (
                    <li
                      key={u.id}
                      className="flex items-center justify-between rounded-lg px-2 py-1.5 text-sm hover:bg-white/5"
                    >
                      <span>
                        {u.name} <span className="text-ink-500">({u.email})</span>
                      </span>
                      <Button type="button" variant="ghost" onClick={() => void inviteUser(u.id)}>
                        Invite
                      </Button>
                    </li>
                  ))}
                </ul>
              </div>

              {status && <p className="text-sm text-accent-400">{status}</p>}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
